import React from 'react';
import './PlaybackControls.css';

export interface PlaybackState {
  /** Whether animation is playing */
  isPlaying: boolean;
  /** Current time position in seconds */
  currentTime: number;
  /** Playback speed multiplier (0.5x, 1x, 2x, etc.) */
  playbackSpeed: number;
  /** Current stroke rate in strokes per minute (SPM) */
  strokeRate: number;
  /** Default stroke rate from data (for reset) */
  defaultStrokeRate: number;
}

export interface PlaybackControlsProps {
  /** Current playback state */
  state: PlaybackState;
  /** Maximum time for scrubber (in seconds) */
  maxTime?: number;
  /** Callback when play/pause is toggled */
  onPlayPause: () => void;
  /** Callback when timeline is scrubbed */
  onTimeChange: (time: number) => void;
  /** Callback when playback speed changes */
  onSpeedChange: (speed: number) => void;
  /** Callback when stroke rate changes */
  onStrokeRateChange: (rate: number) => void;
  /** Callback when reset is clicked */
  onReset: () => void;
}

/**
 * PlaybackControls component - Coach controls for rowing visualization
 *
 * Provides:
 * - Play/Pause button
 * - Timeline scrubber with current time display
 * - Stroke rate adjustment (SPM) with reset
 * - Playback speed control (0.25x to 4x)
 * - Reset all settings button
 */
export const PlaybackControls: React.FC<PlaybackControlsProps> = ({
  state,
  maxTime = 60,
  onPlayPause,
  onTimeChange,
  onSpeedChange,
  onStrokeRateChange,
  onReset,
}) => {
  const { isPlaying, currentTime, playbackSpeed, strokeRate, defaultStrokeRate } = state;

  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Predefined speed options
  const speedOptions = [0.25, 0.5, 1, 1.5, 2, 3, 4];

  // Stroke rate bounds (typical rowing range: 18-44 SPM)
  const minStrokeRate = 10;
  const maxStrokeRate = 50;

  const isStrokeRateModified = Math.abs(strokeRate - defaultStrokeRate) > 0.1;

  return (
    <div className="playback-controls">
      {/* Play/Pause Button */}
      <div className="playback-controls__section">
        <button
          className={`playback-controls__play-button ${isPlaying ? 'playing' : 'paused'}`}
          onClick={onPlayPause}
          aria-label={isPlaying ? 'Pause' : 'Play'}
          title={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="4" width="4" height="16" />
              <rect x="14" y="4" width="4" height="16" />
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>
      </div>

      {/* Timeline Scrubber */}
      <div className="playback-controls__section playback-controls__timeline">
        <span className="playback-controls__time-label">{formatTime(currentTime)}</span>
        <input
          type="range"
          className="playback-controls__slider playback-controls__timeline-slider"
          min={0}
          max={maxTime}
          step={0.1}
          value={currentTime}
          onChange={(e) => onTimeChange(parseFloat(e.target.value))}
          aria-label="Timeline scrubber"
        />
        <span className="playback-controls__time-label">{formatTime(maxTime)}</span>
      </div>

      {/* Stroke Rate Control */}
      <div className="playback-controls__section">
        <label className="playback-controls__label">
          <span className="playback-controls__label-text">
            Stroke Rate: {strokeRate.toFixed(1)} SPM
            {isStrokeRateModified && (
              <span className="playback-controls__modified-indicator">*</span>
            )}
          </span>
          <div className="playback-controls__slider-container">
            <input
              type="range"
              className="playback-controls__slider"
              min={minStrokeRate}
              max={maxStrokeRate}
              step={0.5}
              value={strokeRate}
              onChange={(e) => onStrokeRateChange(parseFloat(e.target.value))}
              aria-label="Stroke rate"
            />
            {isStrokeRateModified && (
              <button
                className="playback-controls__reset-param-button"
                onClick={() => onStrokeRateChange(defaultStrokeRate)}
                title="Reset to data default"
                aria-label="Reset stroke rate to default"
              >
                Reset
              </button>
            )}
          </div>
        </label>
      </div>

      {/* Playback Speed Control */}
      <div className="playback-controls__section">
        <label className="playback-controls__label">
          <span className="playback-controls__label-text">Playback Speed: {playbackSpeed}x</span>
          <div className="playback-controls__speed-buttons">
            {speedOptions.map((speed) => (
              <button
                key={speed}
                className={`playback-controls__speed-button ${
                  playbackSpeed === speed ? 'active' : ''
                }`}
                onClick={() => onSpeedChange(speed)}
                aria-label={`${speed}x speed`}
                aria-pressed={playbackSpeed === speed}
              >
                {speed}x
              </button>
            ))}
          </div>
        </label>
      </div>

      {/* Reset All Button */}
      <div className="playback-controls__section">
        <button
          className="playback-controls__reset-button"
          onClick={onReset}
          aria-label="Reset all settings"
          title="Reset all settings to data defaults"
        >
          Reset All
        </button>
      </div>
    </div>
  );
};
